import React, { useState, useEffect } from "react";
import { CellContext, Table } from "@tanstack/react-table";
import clsx from "clsx";

type TableMeta<TData> = {
  updateData: (rowIndex: number, columnId: string, value: unknown) => void;
};

type TableCellProps<TData> = CellContext<TData, unknown> & {
  table: Table<TData>;
  inputType?: "text" | "number";
};

export function TableCell<TData>({
  getValue,
  row,
  column,
  table,
  inputType,
}: TableCellProps<TData>) {
  const initialValue = getValue() as string;
  const [value, setValue] = useState(initialValue);
  const [edited, setEdited] = useState(false);

  useEffect(() => {
    // reset initial 0 value to empty
    const value =
      inputType === "number" && initialValue === "0" ? "" : initialValue;

    setValue(value);
  }, [initialValue]);

  const onBlur = () => {
    if (inputType === "number" && value !== "") {
      const parsed = parseFloat(value.replace(",", "."));
      if (!isNaN(parsed)) {
        (table.options.meta as TableMeta<TData>)?.updateData(
          row.index,
          column.id,
          parsed
        );
      }
    } else {
      (table.options.meta as TableMeta<TData>)?.updateData(
        row.index,
        column.id,
        value
      );
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    if (inputType === "number") {
      const scientificRegex = /^[+-]?(\d+)?([.,])?(\d+)?([eE][+-]?\d*)?$/;

      if (scientificRegex.test(newValue) || newValue === "") {
        setValue(newValue);
      }
    } else {
      setValue(newValue);
    }
  };

  return (
    <input
      className={clsx(
        "px-2 py-1 w-full border-gray-200 border text-sm font-normal focus-within:border-indigo-600",
        edited ? "border-gray-100" : "border-transparent truncate"
      )}
      value={value || ""}
      onChange={handleChange}
      onBlur={onBlur}
      onClick={(e) => {
        e.stopPropagation();
        setEdited(true);
      }}
      onMouseLeave={() => setEdited(false)}
      type="text"
    />
  );
}
